import {
  Mesh,
  MeshBasicMaterial,
  PointLight,
  SphereGeometry,
  Vector3,
} from 'three';
import { Pool } from '../util/pool.js';
import { segmentIntersectsObstacle } from '../world/obstacles.js';

const geometry = new SphereGeometry(0.18, 10, 8);
const prev = new Vector3();
const dir = new Vector3();

let shotState = null;

export function initEnemyShots(world, options = {}) {
  const capacity = options.capacity ?? 128;
  shotState = {
    world,
    pool: new Pool(createShot, resetShot, 12, capacity),
    shots: [],
  };
  return shotState;
}

export function spawnEnemyShot(enemy, player, game = {}) {
  if (!shotState || !enemy?.alive || !player) return null;
  const shot = shotState.pool.acquire();
  if (!shot) return null;

  dir.copy(player.pos).sub(enemy.pos);
  dir.y = 0;
  if (dir.lengthSq() < 0.01) dir.set(0, 0, 1);
  dir.normalize();

  const wave = game.wave || 1;
  shot.pos.copy(enemy.pos).addScaledVector(dir, enemy.radius + 0.4);
  shot.pos.y = 1.05;
  shot.vel.copy(dir).multiplyScalar(enemy.boss ? 28 : 19 + wave * 0.65);
  shot.damage = enemy.boss ? 18 + wave * 1.25 : 7 + Math.floor(wave * 1.1);
  shot.radius = enemy.boss ? 0.28 : 0.2;
  shot.age = 0;
  shot.ttl = enemy.boss ? 3.8 : 2.9;
  shot.dead = false;
  shot.mesh.scale.setScalar(shot.radius / 0.18);
  shot.mesh.position.copy(shot.pos);
  shot.mesh.visible = true;
  shotState.world.enemyShotRoot.add(shot.mesh);
  shotState.shots.push(shot);
  return shot;
}

export function updateEnemyShots(dt, options = {}) {
  if (!shotState) return;
  const { player, onHit = null } = options;

  for (const shot of shotState.shots) {
    prev.copy(shot.pos);
    shot.age += dt;
    shot.pos.addScaledVector(shot.vel, dt);
    shot.mesh.position.copy(shot.pos);

    if (segmentIntersectsObstacle(prev, shot.pos, shot.radius, shotState.world)) {
      shot.dead = true;
      continue;
    }
    if (shot.age > shot.ttl || Math.abs(shot.pos.x) > shotState.world.half + 4 || Math.abs(shot.pos.z) > shotState.world.half + 4) {
      shot.dead = true;
      continue;
    }
    if (player && segmentDistanceSq(prev, shot.pos, player.pos) < (player.radius + shot.radius) ** 2) {
      shot.dead = true;
      onHit?.(shot, player);
    }
  }

  for (let i = shotState.shots.length - 1; i >= 0; i -= 1) {
    if (!shotState.shots[i].dead) continue;
    releaseShot(shotState.shots[i], i);
  }
}

export function clearEnemyShots() {
  if (!shotState) return;
  for (let i = shotState.shots.length - 1; i >= 0; i -= 1) releaseShot(shotState.shots[i], i);
}

function segmentDistanceSq(start, end, point) {
  const dx = end.x - start.x;
  const dz = end.z - start.z;
  const lenSq = dx * dx + dz * dz;
  if (lenSq <= 0.000001) return point.distanceToSquared(start);
  const t = Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.z - start.z) * dz) / lenSq));
  const x = start.x + dx * t;
  const z = start.z + dz * t;
  return (point.x - x) ** 2 + (point.z - z) ** 2;
}

function createShot() {
  const mesh = new Mesh(
    geometry,
    new MeshBasicMaterial({ color: 0xff70d7, transparent: true, opacity: 0.9 }),
  );
  mesh.add(new PointLight(0xff70d7, 1.4, 5, 2));
  return {
    mesh,
    pos: new Vector3(),
    vel: new Vector3(),
    dead: true,
  };
}

function resetShot(shot) {
  shot.mesh.visible = false;
  shot.pos.set(0, 0, 0);
  shot.vel.set(0, 0, 0);
  shot.dead = true;
}

function releaseShot(shot, index) {
  shotState.world.enemyShotRoot.remove(shot.mesh);
  shotState.shots.splice(index, 1);
  shotState.pool.release(shot);
}
